import { Injectable, signal } from '@angular/core';
import { get } from 'lodash-es';
import { BehaviorSubject, Observable } from 'rxjs';

import { LanguagesService } from '@shure/shared/angular/utils/i18n';

import {
	AccountInformationDto,
	MaintenanceConfigData,
	mapOktaLanguageCode,
	OrganizationsResponse,
	OrgData
} from './organizations-store.model';
@Injectable({
	providedIn: 'root'
})
export class OrganizationsStoreService {
	private selectedOrganization = new BehaviorSubject({ isOrgSelected: false, orgName: '', orgId: '', orgNumber: '' });
	public organizationList!: OrganizationsResponse[];
	private defaultOrganization = new BehaviorSubject({ orgId: '', orgName: '' });
	public appVersion = new BehaviorSubject('');
	public orderOfNamesList$ = new BehaviorSubject<{ label: string; id: string }[]>([]);
	public maintenanceModeInfo = new BehaviorSubject<MaintenanceConfigData | null>(null);
	public maintenanceModeInfoFe = new BehaviorSubject<MaintenanceConfigData | null>(null);
	public userSession!: { string: boolean };
	private userRole!: string;
	private orgId!: string;
	private accountId = '';
	public readonly accountInformation = signal<AccountInformationDto>({});
	private toggleFeatureFlag = false;
	public toggleFeatureFlag$: BehaviorSubject<boolean> = new BehaviorSubject(false);
	public displayDataTransferConsent = signal<boolean | null>(null);

	constructor(public localizationService: LanguagesService) {
		// If page got refreshed, get the selected organization detail from session
		const selectedOrganization = navigator.cookieEnabled ? sessionStorage.getItem('selectedOrganization') : '';
		if (selectedOrganization) {
			const selectedOrgData = JSON.parse(selectedOrganization);
			this.selectedOrganization.next(selectedOrgData);
		}
	}

	public updateDataTransferConsent(isDisplay: boolean): void {
		this.displayDataTransferConsent.set(isDisplay);
	}

	/**
	 * stores the permissions
	 * @param organization - OrganizationsResponse representation of org data,
	 * if params is undefined or null, then an empty array will be used as the default value.
	 */
	public storePermissions(permissions: { string: boolean }): void {
		this.userSession = permissions;
	}

	/**
	 * stores the role
	 * @param role - string representation of user role data,
	 */
	public storeUserRole(role: string): void {
		this.userRole = role;
	}

	/**
	 * This will return the user role
	 */
	public getUserRole(): string {
		return this.userRole;
	}

	/**
	 * stores the OrgId
	 * @param OrgId - string representation of user OrgId data,
	 */
	public setOrgId(orgId: string): void {
		this.orgId = orgId;
	}

	/**
	 * This will return the orgId
	 */
	public getOrgId(): string {
		return this.orgId;
	}

	public testPermission(requiredPermission: string): boolean {
		return get(this.userSession, requiredPermission) === true;
	}

	/**
	 * This function will only toggle a single flag. This as of now is in use by ElevatedPrivileges
	 */
	public toggleFeature(): void {
		this.toggleFeatureFlag = !this.toggleFeatureFlag;
		this.toggleFeatureFlag$.next(this.toggleFeatureFlag);
	}
	/**
	 * The toggle flag observable
	 */
	public getToggleFeature$(): Observable<boolean> {
		return this.toggleFeatureFlag$;
	}
	/**
	 * gets the selected organization data as observable
	 * @returns Observable that provides selected organization updates
	 */
	public getSelectedOrganization$(): Observable<OrgData> {
		return this.selectedOrganization.asObservable();
	}
	/**
	 * Sets selected organization data
	 * @param isOrgSelected - boolean representation of organization selection status
	 * @param orgName - string representation of organization name
	 * @param orgId - string representation of organization id
	 */
	public setSelectedOrganization(isOrgSelected: boolean, orgName: string, orgId: string, orgNumber = ''): void {
		const selectedOrgData = {
			isOrgSelected: isOrgSelected,
			orgName: orgName,
			orgId: orgId,
			orgNumber: orgNumber
		};
		sessionStorage.setItem('selectedOrganization', JSON.stringify(selectedOrgData));
		this.selectedOrganization.next(selectedOrgData);
	}

	public setVersion(version: string): void {
		this.appVersion.next(version);
	}

	public getVersion(): Observable<string> {
		return this.appVersion.asObservable();
	}

	/**
	 * set maintenance mode information
	 * @param maintenance - Maintenance representation of the received maintenance mode information
	 */
	public setMaintenance(maintenance: MaintenanceConfigData): void {
		this.maintenanceModeInfo.next(maintenance);
	}

	/**
	 * gets the stored maintenance mode information
	 */
	public getMaintenance(): Observable<MaintenanceConfigData | null> {
		return this.maintenanceModeInfo.asObservable();
	}

	/**
	 * Sets the complete account information into the state
	 * @param accountInfo - AccountInformationDto representation of api response from session/account-info api
	 */

	public setAccountInfo(accountInfo: AccountInformationDto): void {
		this.accountInformation.set(accountInfo);
		const accountInfoDetails = accountInfo.preferences;
		if (accountInfoDetails) {
			if (accountInfoDetails.language) {
				const accountLanguage = mapOktaLanguageCode[accountInfoDetails.language] || 'en_US';
				localStorage.setItem('locale', accountLanguage);
				this.setLocale(accountLanguage);
				this.localizationService.getPreferredCultureCode();
			}
			if (accountInfoDetails.dateFormat) {
				this.setDateFormat(accountInfoDetails.dateFormat);
			}
			if (accountInfoDetails.orderOfNames) {
				this.setOrderOfNames(JSON.stringify(accountInfoDetails.orderOfNames));
			}
			if (accountInfoDetails.timeZone) {
				this.setTimeZone(accountInfoDetails.timeZone);
			}
		}
	}

	/**
	 * Updates the account information in the state
	 * @param accountInfo - partial representation of AccountInformationDto to update in state
	 */
	public updateAccountInfo(accountInfo: Partial<AccountInformationDto>): void {
		this.accountInformation.set({ ...this.accountInformation(), ...accountInfo });
	}

	/**
	 * Sets an item in localStorage.
	 * @param key - The key under which the value will be stored in localStorage.
	 * @param value - The string value to store in localStorage.
	 */
	public setLocalStorageItem(key: string, value: string): void {
		localStorage.setItem(key, value);
	}

	/**
	 * Retrieves an item from localStorage.
	 * @param key - The key associated with the value to retrieve from localStorage.
	 * @returns The stored value as a string or null if not found.
	 */
	public getLocalStorageItem(key: string): string | null {
		return localStorage.getItem(key);
	}

	/**
	 * Stores the locale in localStorage.
	 * @param locale - The locale string to store (e.g., 'en-US').
	 */
	public setLocale(locale: string): void {
		this.setLocalStorageItem('locale', locale);
		//Update the document language, to support multi language for cookiebot dialog
		document.documentElement.lang = locale === 'zh-cn' ? 'zh' : locale;
	}

	/**
	 * Retrieves the stored locale from localStorage.
	 * @returns The stored locale as a string or null if not found.
	 */
	public getLocale(): string | null {
		return this.getLocalStorageItem('locale');
	}

	/**
	 * Stores the order of names preference in localStorage.
	 * @param order - The order preference string to store (e.g., 'firstLast').
	 */
	public setOrderOfNames(order: string): void {
		const orderOfNames = JSON.parse(order);
		if (orderOfNames && Array.isArray(orderOfNames)) {
			const orderOfNamesList = orderOfNames.map((item: string) => ({
				id: item,
				label: item === 'firstName' ? 'first-name' : 'last-name'
			}));
			this.orderOfNamesList$.next(orderOfNamesList);
		}
		this.setLocalStorageItem('orderOfNames', order);
	}

	/**
	 * Retrieves the stored order of names preference from localStorage.
	 * @returns The stored order preference as a string or null if not found.
	 */
	public getOrderOfNames(): string | null {
		return this.getLocalStorageItem('orderOfNames');
	}

	/**
	 * Stores the date format in localStorage.
	 * @param format - The date format string to store (e.g., 'MM/DD/YYYY').
	 */
	public setDateFormat(format: string): void {
		this.setLocalStorageItem('dateFormat', format);
	}

	/**
	 * Retrieves the stored date format from localStorage.
	 * @returns The stored date format as a string or null if not found.
	 */
	public getDateFormat(): string | null {
		return this.getLocalStorageItem('dateFormat');
	}

	/**
	 * Stores the time zone in localStorage.
	 * @param timeZone - The time zone string to store (e.g., 'America/Panama').
	 */
	public setTimeZone(timeZone: string): void {
		this.setLocalStorageItem('timeZone', timeZone);
	}

	/**
	 * Retrieves the stored time zone from localStorage.
	 * @returns The stored time zone as a string or null if not found.
	 */
	public getTimeZone(): string | null {
		return this.getLocalStorageItem('timeZone');
	}
}
